import React from 'react';
import jsPDF from 'jspdf';
import styles from './ClientTable.module.css';

const formatCurrency = (amount) => {
  const numAmount = parseFloat(amount || 0);
  if (isNaN(numAmount)) return amount;
  
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0
  }).format(numAmount);
};

const formatDate = (dateStr) => {
  if (!dateStr) return 'TBD';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  
  const days = d.getDate();
  const month = d.toLocaleString('en-US', { month: 'long' });
  const year = d.getFullYear();
  return `${days} ${month} ${year}`;
};

const ClientTable = ({ clients, onAddPayment, onEditClient, onDeleteClient, onToggleStatus, onViewSummary }) => {
  const getDynamicStatus = (client) => {
    if (client.status === 'inactive') return 'Inactive';
    if (client.remainingAmount <= 0 && client.totalAmount > 0) return 'Paid in Full';
    
    let nextDue;
    if (client.nextDueDate) {
      nextDue = new Date(client.nextDueDate);
    } else {
      nextDue = new Date(client.lastPaymentDate || client.createdAt || client.startDate);
      nextDue.setDate(nextDue.getDate() + 15);
    }
    
    if (isNaN(nextDue.getTime())) return client.status || 'Pending';
    
    const today = new Date();
    today.setHours(0,0,0,0);
    nextDue.setHours(0,0,0,0);
    
    const diffTime = nextDue.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return 'Overdue';
    if (diffDays <= 5) return 'Pending';
    return 'Received';
  };

  const getStatusClass = (statusStr) => {
    if (statusStr === 'Inactive') return ''; // we will use inline style or default gray
    if (statusStr === 'Paid in Full' || statusStr === 'Received') return styles.statusCompleted;
    if (statusStr === 'Pending') return styles.statusPending;
    if (statusStr === 'Overdue') return styles.statusOverdue;
    return styles.statusPending;
  };

  const generatePDF = (client) => {
    const doc = new jsPDF();
    doc.setFontSize(24);
    doc.setTextColor(15, 23, 42); // slate-900
    doc.text("Ananta CRM", 105, 30, { align: 'center' });
    
    doc.setFontSize(14);
    doc.setTextColor(71, 85, 105); // slate-600
    doc.text("Client Payment Notice", 105, 40, { align: 'center' });
    
    doc.setDrawColor(226, 232, 240); // slate-200
    doc.line(20, 50, 190, 50);
    
    doc.setFontSize(12);
    doc.setTextColor(15, 23, 42);
    doc.text(`Client Name: ${client.clientName || client.name || 'Unknown'}`, 25, 70);
    doc.text(`Channel Name: ${client.channelName || client.channel || 'N/A'}`, 25, 80);
    
    const joiningDate = formatDate(client.startDate || client.createdAt);
    doc.text(`Joining Date: ${joiningDate}`, 25, 100);
    
    let nextDue;
    if (client.nextDueDate) nextDue = client.nextDueDate;
    else {
      const d = new Date(client.lastPaymentDate || client.createdAt || client.startDate);
      d.setDate(d.getDate() + 15);
      nextDue = d.toISOString();
    }
    const dueDateStr = formatDate(nextDue);
    const dynamicStatus = getDynamicStatus(client);
    
    doc.text(`Next Due Date: ${dueDateStr}`, 25, 110);
    doc.text(`Current Status: ${dynamicStatus}`, 25, 120);
    
    doc.setDrawColor(226, 232, 240);
    doc.line(20, 140, 190, 140);
    
    doc.setFontSize(16);
    if (client.remainingAmount > 0) {
      doc.setTextColor(239, 68, 68); // red-500
      doc.text(`Pending Amount: ${formatCurrency(client.remainingAmount)}`, 105, 160, { align: 'center' });
    } else {
      doc.setTextColor(16, 185, 129); // emerald-500
      doc.text(`Status: Paid in Full`, 105, 160, { align: 'center' });
    }
    
    doc.setFontSize(10);
    doc.setTextColor(148, 163, 184); // slate-400
    doc.text("Generated via Ananta CRM System", 105, 280, { align: 'center' });
    
    doc.save(`${client.clientName || client.name || 'Client'}_Notice.pdf`);
  };

  const activeClients = clients.filter(c => c.status !== 'inactive');
  const totalAmountSum = activeClients.reduce((sum, c) => sum + parseFloat(c.totalAmount || 0), 0);
  const receivedAmountSum = activeClients.reduce((sum, c) => sum + parseFloat(c.receivedAmount || 0), 0);
  const remainingAmountSum = activeClients.reduce((sum, c) => sum + parseFloat(c.remainingAmount || 0), 0);

  return (
    <div className={styles.tableContainer}>
      <div className={styles.tableHeader}>
        <h2 className={styles.tableTitle}>Recent Clients</h2>
      </div>
      <div className={styles.tableResponsive}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={styles.th}>#</th>
              <th className={styles.th}>Channel / Client</th>
              <th className={styles.th}>Start Date</th>
              <th className={styles.th}>Due Date</th>
              <th className={styles.th}>Status</th>
              <th className={styles.th}>Total</th>
              <th className={styles.th}>Received</th>
              <th className={styles.th}>Remaining</th>
              {onAddPayment && <th className={styles.th}>Action</th>}
            </tr>
          </thead>
          <tbody>
            {clients && clients.length > 0 ? (
              <>
                {clients.map((client, index) => {
                const dynamicStatus = getDynamicStatus(client);
                return (
                  <tr key={client.id} className={styles.tr}>
                    <td className={styles.td}>{index + 1}</td>
                    <td className={styles.td}>
                      <div className={styles.clientInfo}>
                        <span className={styles.clientName}>{client.channelName || client.channel || "No Channel"}</span>
                        <span className={styles.channelName}>{client.clientName || client.name}</span>
                      </div>
                    </td>
                    <td className={styles.td}>{formatDate(client.startDate)}</td>
                    <td className={styles.td}>{formatDate(client.nextDueDate || client.dueDate)}</td>
                    <td className={styles.td}>
                      <span className={`${styles.statusBadge} ${getStatusClass(dynamicStatus)}`} style={dynamicStatus === 'Inactive' ? { backgroundColor: '#e2e8f0', color: '#64748b' } : {}}>
                        {dynamicStatus}
                      </span>
                    </td>
                    <td className={styles.td}>
                      <span className={styles.amount}>
                        {formatCurrency(client.totalAmount)}
                      </span>
                    </td>
                    <td className={styles.td}>
                      <span className={styles.amount}>
                        {formatCurrency(client.receivedAmount || 0)}
                      </span>
                    </td>
                    <td className={styles.td}>
                      <span className={styles.amount}>
                        {formatCurrency(client.remainingAmount || 0)}
                      </span>
                    </td>
                    {onAddPayment && (
                      <td className={styles.td}>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                          {client.status !== 'inactive' && (
                            <>
                              {client.remainingAmount > 0 ? (
                                <button 
                                  className={styles.actionBtn}
                                  onClick={() => onAddPayment(client)}
                                >
                                  + Payment
                                </button>
                              ) : (
                                <span style={{ fontSize: '0.8rem', color: '#10b981', fontWeight: 600 }}>Paid in Full</span>
                              )}
                            </>
                          )}
                          
                          {onEditClient && client.status !== 'inactive' && (
                            <button 
                              className={styles.editBtn}
                              onClick={() => onEditClient(client)}
                            >
                              Edit
                            </button>
                          )}

                          {onViewSummary && (
                            <button 
                              className={styles.editBtn}
                              onClick={() => onViewSummary(client)}
                              style={{ backgroundColor: '#64748b', color: 'white', border: 'none' }}
                            >
                              Summary
                            </button>
                          )}

                          <button 
                            className={styles.editBtn}
                            onClick={() => generatePDF(client)}
                            style={{ backgroundColor: '#3b82f6', color: 'white', border: 'none' }}
                          >
                            Share PDF
                          </button>

                          {onToggleStatus && (
                            <button 
                              className={styles.editBtn}
                              onClick={() => onToggleStatus(client)}
                              style={{ backgroundColor: client.status === 'inactive' ? '#10b981' : '#f59e0b', color: 'white', border: 'none' }}
                            >
                              {client.status === 'inactive' ? 'Activate' : 'Deactivate'}
                            </button>
                          )}

                          {onDeleteClient && (
                            <button 
                              className={styles.actionBtn}
                              onClick={() => onDeleteClient(client.id)}
                              style={{ backgroundColor: '#ef4444', color: 'white', border: 'none' }}
                            >
                              Delete
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
              <tr className={styles.tr} style={{ backgroundColor: '#f8fafc', borderTop: '2px solid #e2e8f0' }}>
                <td className={styles.td} colSpan="5" style={{ textAlign: 'right', fontWeight: 'bold', color: '#0f172a' }}>Total Sum:</td>
                <td className={styles.td} style={{ fontWeight: 'bold', color: '#0f172a' }}>{formatCurrency(totalAmountSum)}</td>
                <td className={styles.td} style={{ fontWeight: 'bold', color: '#10b981' }}>{formatCurrency(receivedAmountSum)}</td>
                <td className={styles.td} style={{ fontWeight: 'bold', color: remainingAmountSum > 0 ? '#ef4444' : '#0f172a' }}>{formatCurrency(remainingAmountSum)}</td>
                <td className={styles.td}></td>
              </tr>
              </>
            ) : (
              <tr>
                <td colSpan={onAddPayment ? "9" : "8"} className={styles.td} style={{ textAlign: 'center', color: '#94a3b8' }}>
                  No clients found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ClientTable;
